import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import { api } from "../lib/api";
import { Loader2, Eye, MousePointerClick, Smartphone, Monitor, Tablet, Globe, ArrowUpRight } from "lucide-react";

const COLORS = ["#b8904a", "#f5d27a", "#e2b56f", "#8a6c39", "#5e431a", "#cfa05c", "#a17a3b", "#d4b274"];

const fmtN = (n) => Number(n || 0).toLocaleString();

const DEVICE_ICONS = { mobile: Smartphone, tablet: Tablet, desktop: Monitor, unknown: Globe };

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="iq-card p-5 flex items-start gap-4">
      <div className="h-10 w-10 inline-flex items-center justify-center bg-gold/10 border border-gold/30 rounded-sm text-gold shrink-0">
        <Icon className="h-5 w-5" strokeWidth={1.8} />
      </div>
      <div className="min-w-0">
        <div className="iq-overline text-cream-dim/70 mb-1">{label}</div>
        <div className="font-display text-cream text-3xl tracking-wider leading-none">{value}</div>
        {sub && <div className="text-cream-dim text-xs font-serif italic mt-1">{sub}</div>}
      </div>
    </div>
  );
}

/* Reusable mini-table */
function MiniTable({ rows, columns, empty }) {
  if (!rows.length) {
    return <div className="text-cream-dim/60 text-sm font-serif italic py-6 text-center">{empty}</div>;
  }
  return (
    <div className="space-y-1">
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-12 gap-3 items-center text-xs px-2 py-2 hover:bg-panel-border/20 rounded-sm">
          {columns.map((col, ci) => (
            <div key={ci} className={col.className || `col-span-${col.span || 4}`}>
              {col.render ? col.render(r) : r[col.key]}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function TrafficTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setData(null);
    api.get("/admin/analytics/traffic", { params: { days } }).then((r) => setData(r.data));
  }, [days]);

  if (!data) {
    return <div className="iq-card p-12 text-center"><Loader2 className="h-8 w-8 text-gold mx-auto animate-spin" /></div>;
  }

  const deviceRows = Object.entries(data.device_split || {}).map(([k, n]) => ({ k, n }));
  const totalDev = deviceRows.reduce((s, r) => s + r.n, 0) || 1;

  return (
    <div data-testid="admin-traffic-panel" className="space-y-6">
      {/* Window selector */}
      <div className="flex items-center gap-2">
        <span className="iq-overline text-cream-dim/70">Window:</span>
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            data-testid={`admin-traffic-window-${d}`}
            className={`px-3 py-1 rounded-sm text-xs font-display tracking-widest border transition-colors ${
              days === d ? "bg-gold text-ink border-gold" : "text-cream-dim border-panel-border hover:border-gold/40"
            }`}
          >
            {d}d
          </button>
        ))}
      </div>

      {/* Headline stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Eye} label="Total views" value={fmtN(data.total_views)} sub={`last ${days} days`} />
        <Stat icon={Globe} label="Unique visitors" value={fmtN(data.unique_visitors)} sub="rolling daily fingerprint" />
        <Stat
          icon={Monitor}
          label="Pages tracked"
          value={fmtN(data.top_pages?.length || 0)}
          sub={`top: ${data.top_pages?.[0]?.path || "—"}`}
        />
        <Stat
          icon={ArrowUpRight}
          label="Referrers"
          value={fmtN(data.top_referrers?.length || 0)}
          sub={data.top_referrers?.[0]?.ref ? new URL(data.top_referrers[0].ref).hostname : "no external refs yet"}
        />
      </div>

      {/* Daily line chart */}
      <div className="iq-card p-5">
        <div className="iq-overline mb-3">Views & uniques per day</div>
        {data.daily.length === 0 ? (
          <div className="text-cream-dim/60 text-sm font-serif italic py-12 text-center">
            No traffic in the last {days} days yet — visit the public site to seed some data.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a283a" />
              <XAxis dataKey="day" stroke="#6e7c91" fontSize={10} />
              <YAxis stroke="#6e7c91" fontSize={10} />
              <Tooltip
                contentStyle={{ background: "#0d1525", border: "1px solid #1a283a", fontSize: 12 }}
                labelStyle={{ color: "#f5d27a" }}
              />
              <Line type="monotone" dataKey="views" stroke="#f5d27a" strokeWidth={2} dot={false} name="Views" />
              <Line type="monotone" dataKey="uniques" stroke="#b8904a" strokeWidth={2} dot={false} name="Uniques" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Top pages */}
        <div className="iq-card p-5">
          <div className="iq-overline mb-3">Top pages</div>
          <MiniTable
            rows={data.top_pages}
            empty="No page data yet."
            columns={[
              {
                span: 8,
                render: (r) => <span className="text-cream font-mono text-xs truncate block">{r.path}</span>,
              },
              {
                span: 2,
                className: "col-span-2 text-cream-dim text-right",
                render: (r) => fmtN(r.uniques) + " u",
              },
              {
                span: 2,
                className: "col-span-2 text-gold text-right font-display",
                render: (r) => fmtN(r.n),
              },
            ]}
          />
        </div>

        {/* Top referrers */}
        <div className="iq-card p-5">
          <div className="iq-overline mb-3">Top referrers (external)</div>
          <MiniTable
            rows={data.top_referrers}
            empty="No external referrers yet."
            columns={[
              {
                span: 10,
                render: (r) => {
                  let host = r.ref;
                  try { host = new URL(r.ref).hostname; } catch { /* ignore */ }
                  return (
                    <a href={r.ref} target="_blank" rel="noopener noreferrer" className="text-cream hover:text-gold truncate block">
                      {host}
                    </a>
                  );
                },
              },
              {
                span: 2,
                className: "col-span-2 text-gold text-right font-display",
                render: (r) => fmtN(r.n),
              },
            ]}
          />
        </div>

        {/* Device breakdown */}
        <div className="iq-card p-5">
          <div className="iq-overline mb-3">Device</div>
          {deviceRows.length === 0 ? (
            <div className="text-cream-dim/60 text-sm font-serif italic py-6 text-center">No data yet.</div>
          ) : (
            <div className="space-y-2">
              {deviceRows.map((r) => {
                const Icon = DEVICE_ICONS[r.k] || Globe;
                const pct = Math.round((r.n / totalDev) * 100);
                return (
                  <div key={r.k} className="flex items-center gap-3">
                    <Icon className="h-4 w-4 text-gold/70 shrink-0" />
                    <span className="text-cream text-sm capitalize w-20">{r.k}</span>
                    <div className="flex-1 h-2 bg-ink rounded-sm overflow-hidden">
                      <div className="h-full bg-gold" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-cream-dim text-xs w-12 text-right">{pct}%</span>
                    <span className="text-gold text-xs w-12 text-right font-display">{fmtN(r.n)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Country breakdown */}
        <div className="iq-card p-5">
          <div className="iq-overline mb-3">Countries (needs CF-IPCountry header)</div>
          {data.countries.length === 0 ? (
            <div className="text-cream-dim/60 text-sm font-serif italic py-6 text-center">
              No geo data yet. Enable Cloudflare's <span className="font-mono">CF-IPCountry</span> header for country breakdowns.
            </div>
          ) : (
            <MiniTable
              rows={data.countries}
              columns={[
                { span: 10, render: (r) => <span className="text-cream font-display tracking-widest">{r.country}</span> },
                { span: 2, className: "col-span-2 text-gold text-right font-display", render: (r) => fmtN(r.n) },
              ]}
              empty=""
            />
          )}
        </div>
      </div>
    </div>
  );
}

export function AffiliatesTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setData(null);
    api.get("/admin/analytics/affiliates", { params: { days } }).then((r) => setData(r.data));
  }, [days]);

  if (!data) {
    return <div className="iq-card p-12 text-center"><Loader2 className="h-8 w-8 text-gold mx-auto animate-spin" /></div>;
  }

  return (
    <div data-testid="admin-affiliates-panel" className="space-y-6">
      <div className="flex items-center gap-2">
        <span className="iq-overline text-cream-dim/70">Window:</span>
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            data-testid={`admin-affiliates-window-${d}`}
            className={`px-3 py-1 rounded-sm text-xs font-display tracking-widest border transition-colors ${
              days === d ? "bg-gold text-ink border-gold" : "text-cream-dim border-panel-border hover:border-gold/40"
            }`}
          >
            {d}d
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={MousePointerClick} label="Total clicks" value={fmtN(data.total_clicks)} sub={`last ${days} days`} />
        <Stat
          icon={Globe}
          label="Active networks"
          value={fmtN(data.by_network?.length || 0)}
          sub={data.by_network?.[0]?.network || "no clicks yet"}
        />
        <Stat
          icon={ArrowUpRight}
          label="Top placement"
          value={data.by_source?.[0]?.source || "—"}
          sub={data.by_source?.[0] ? `${fmtN(data.by_source[0].n)} clicks` : "no data"}
        />
        <Stat
          icon={MousePointerClick}
          label="Est. revenue floor"
          value={`€${fmtN(Math.round(data.total_clicks * 0.04 * 50))}`}
          sub="rough est: clicks × 4% × €50 AOV"
        />
      </div>

      {/* Daily clicks */}
      <div className="iq-card p-5">
        <div className="iq-overline mb-3">Clicks per day</div>
        {data.daily.length === 0 ? (
          <div className="text-cream-dim/60 text-sm font-serif italic py-12 text-center">
            No affiliate clicks tracked yet — click any "Book on …" link on the public site to seed data.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a283a" />
              <XAxis dataKey="day" stroke="#6e7c91" fontSize={10} />
              <YAxis stroke="#6e7c91" fontSize={10} />
              <Tooltip
                contentStyle={{ background: "#0d1525", border: "1px solid #1a283a", fontSize: 12 }}
                labelStyle={{ color: "#f5d27a" }}
              />
              <Bar dataKey="clicks" fill="#b8904a" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* By network */}
        <div className="iq-card p-5">
          <div className="iq-overline mb-3">By network</div>
          {data.by_network.length === 0 ? (
            <div className="text-cream-dim/60 text-sm font-serif italic py-6 text-center">No clicks yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.by_network} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#1a283a" />
                <XAxis type="number" stroke="#6e7c91" fontSize={10} />
                <YAxis type="category" dataKey="network" stroke="#6e7c91" fontSize={11} width={90} />
                <Tooltip
                  contentStyle={{ background: "#0d1525", border: "1px solid #1a283a", fontSize: 12 }}
                  labelStyle={{ color: "#f5d27a" }}
                />
                <Bar dataKey="n" name="Clicks">
                  {data.by_network.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* By placement / source */}
        <div className="iq-card p-5">
          <div className="iq-overline mb-3">By placement (source)</div>
          <MiniTable
            rows={data.by_source}
            empty="No clicks yet."
            columns={[
              {
                span: 10,
                render: (r) => <span className="text-cream font-mono text-xs">{r.source}</span>,
              },
              {
                span: 2,
                className: "col-span-2 text-gold text-right font-display",
                render: (r) => fmtN(r.n),
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
}
